/* Skeleton shown in the toast the instant the shopper clicks add, before the network responds. */
const CN_SKELETON =
  '<div class="cn-skel cn-skel-img"></div>' +
  '<div class="cn-product__body">' +
  '<span class="cn-skel cn-skel-line" style="width:40%"></span>' +
  '<span class="cn-skel cn-skel-line" style="width:85%"></span>' +
  '<span class="cn-skel cn-skel-line" style="width:55%"></span>' +
  '</div>';

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.dismissTimer = null;
    this.closingTimer = null;
    this.pendingSafety = null;
    this.scrollArmed = false;
    this.openScrollY = 0;
    this.isPending = false;
    this.pendingAborted = false;
    this.DISMISS_MS = 6000;

    this.titleEl = this.querySelector('.cn-title');
    this.titleAdded = this.titleEl ? this.titleEl.textContent.trim() : 'Added to cart';

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    ['mouseenter', 'pointerdown', 'touchstart', 'focusin'].forEach((evt) =>
      this.addEventListener(evt, () => this.pauseDismiss(), { passive: true })
    );
    ['mouseleave', 'pointerup', 'touchend', 'pointercancel', 'focusout'].forEach((evt) =>
      this.addEventListener(evt, () => this.resumeDismiss())
    );

    document.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape' && this.notification.classList.contains('active')) this.close();
    });
  }

  /* Show the toast IMMEDIATELY on click (loading state), before the /cart/add.js round-trip returns,
     so there's no dead "greyed out" wait. renderContents() fills the real content when it lands. */
  openPending() {
    this.isPending = true;
    this.pendingAborted = false;
    const prod = document.getElementById('cart-notification-product');
    const fs = document.getElementById('cart-notification-freeship');
    if (prod) prod.innerHTML = CN_SKELETON;
    if (fs) fs.innerHTML = '<span class="cn-skel cn-skel-bar"></span>';
    if (this.titleEl) this.titleEl.textContent = 'Adding to cart…';
    this.reveal(true);
    clearTimeout(this.pendingSafety);
    this.pendingSafety = setTimeout(() => {
      if (this.isPending) this.cancelPending();
    }, 12000);
  }

  /* The add failed (sold out / network) — retract the optimistic toast. */
  cancelPending() {
    clearTimeout(this.pendingSafety);
    if (!this.isPending) return;
    this.isPending = false;
    if (this.titleEl) this.titleEl.textContent = this.titleAdded;
    this.close();
  }

  reveal(pending) {
    const showing = this.notification.classList.contains('active');
    if (!showing) {
      clearTimeout(this.closingTimer);
      this.notification.classList.remove('cn-closing', 'cn-paused');
      void this.notification.offsetWidth;
      this.notification.classList.add('animate', 'active');

      document.body.addEventListener('click', this.onBodyClick);

      this.openScrollY = window.scrollY;
      this.scrollArmed = false;
      setTimeout(() => {
        this.scrollArmed = true;
      }, 450);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    this.notification.classList.toggle('cn-pending', !!pending);
    if (!pending) this.startDismissTimer();
  }

  open() {
    this.reveal(false);
  }

  close() {
    clearTimeout(this.dismissTimer);
    clearTimeout(this.pendingSafety);
    document.body.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('scroll', this.onScroll);
    if (!this.notification.classList.contains('active')) return;

    // If the shopper dismisses while we're still loading, remember it so the late response
    // doesn't pop the toast back up.
    if (this.isPending) this.pendingAborted = true;
    this.isPending = false;
    if (this.titleEl) this.titleEl.textContent = this.titleAdded;

    // Fade out IN PLACE while still catching taps, so a tap mid-dismiss can't hit the item behind it.
    this.notification.classList.add('cn-closing');
    this.notification.classList.remove('active', 'cn-paused', 'cn-pending');
    clearTimeout(this.closingTimer);
    this.closingTimer = setTimeout(() => this.notification.classList.remove('cn-closing'), 350);
  }

  pauseDismiss() {
    clearTimeout(this.dismissTimer);
    this.notification.classList.add('cn-paused');
  }

  resumeDismiss() {
    this.notification.classList.remove('cn-paused');
    if (this.notification.classList.contains('active') && !this.isPending) this.startDismissTimer();
  }

  startDismissTimer() {
    clearTimeout(this.dismissTimer);
    this.dismissTimer = setTimeout(() => this.close(), this.DISMISS_MS);
  }

  handleScroll() {
    if (this.scrollArmed && Math.abs(window.scrollY - this.openScrollY) > 40) this.close();
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const wasAborted = this.pendingAborted;
    this.isPending = false;
    this.pendingAborted = false;
    clearTimeout(this.pendingSafety);
    if (this.titleEl) this.titleEl.textContent = this.titleAdded;

    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el) return;
      el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.header) this.header.reveal();
    if (wasAborted) return; // shopper dismissed mid-load — fill silently, don't pop it back up
    this.reveal(false);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-freeship',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
